import { loadRazorpayScript } from '@/src/lib/utils';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { Input } from '@/src/components/ui/input';
import { getSupabaseClient } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getOfficialModuleName } from '../data';
import { usePlans } from '../hooks/usePlans';
import { ChevronDown, Mail, CheckCircle2, Loader2, X } from 'lucide-react';

const getBusinessTypeFromModule = (moduleName: string) => {
  const map: Record<string, string> = {
    'Retail POS': 'retail',
    'Manufacturing ERP': 'manufacturing',
    'Hotel/Restaurant': 'hospitality',
    'Health Care': 'healthcare',
    'Education': 'education',
    'Transport Management': 'logistics',
    'Agri Management': 'agri',
    'Daily Services': 'services'
  };
  return map[moduleName] || 'retail';
};

// Helper to invoke Supabase Edge Functions with fallback to direct HTTP fetch
const invokeEdgeFunction = async (functionName: string, body: any) => {
  const supabase = getSupabaseClient();
  if (supabase) {
    try {
      const { data, error } = await supabase.functions.invoke(functionName, { body });
      if (error) {
        let errMsg = error.message;
        if ((error as any).context) {
          try {
            const errJson = await (error as any).context.json();
            if (errJson && errJson.error) errMsg = errJson.error;
          } catch (_) {}
        }
        return { data: null, error: errMsg };
      }
      return { data, error: null };
    } catch (e: any) {
      // Fallback below
    }
  }

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://rdioqcpluulhpxnclhfp.supabase.co';
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const res = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
        'Authorization': `Bearer ${anonKey}`
      },
      body: JSON.stringify(body)
    });
    const resJson = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { data: null, error: resJson.error || `Request failed with status ${res.status}` };
    }
    return { data: resJson, error: null };
  } catch (err: any) {
    return { data: null, error: err.message || 'Network error occurred' };
  }
};

export default function Checkout() {
  useDocumentTitle('BahiBox | Checkout');
  const navigate = useNavigate();
  const { user, currentTenantId } = useAuth();
  const [searchParams] = useSearchParams();
  const moduleName = searchParams.get('module') || 'General';
  const plan = searchParams.get('plan') || 'Free';
  const initialCycle = searchParams.get('cycle') || 'monthly';
  const [selectedCycle, setSelectedCycle] = useState(initialCycle);
  const { plans, loading: plansLoading } = usePlans();
  const matchedPlan = plans.find(p => p.id === plan) || plans.find(p => p.name === plan && p.moduleName === moduleName);
  const displayPlanName = matchedPlan ? matchedPlan.name : plan;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    businessName: '',
    address: '',
    pincode: '',
    password: '',
    confirmPassword: ''
  });
  
  const [pincodeError, setPincodeError] = useState('');
  const [isCheckingPincode, setIsCheckingPincode] = useState(false);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [actualPrice, setActualPrice] = useState(0);
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [gstPercentage, setGstPercentage] = useState(18);
  const [globalDiscountType, setGlobalDiscountType] = useState<'none' | 'percent' | 'fixed'>('none');
  const [globalDiscountValue, setGlobalDiscountValue] = useState(0);

  // OTP Modal & Signup States (Steps 1 - 5)
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [pendingSignupId, setPendingSignupId] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState<{
    title: string;
    message: string;
    email?: string;
    autoRedirect?: boolean;
  } | null>(null);

  // Auto redirect for paid plan success
  useEffect(() => {
    if (signupSuccess?.autoRedirect) {
      const timer = setTimeout(() => {
        navigate('/login');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [signupSuccess, navigate]);

  useEffect(() => {
    const fetchPlatformCheckoutSettings = async () => {
      const supabase = getSupabaseClient();
      if (!supabase) return;
      const { data } = await supabase
        .from('platform_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['checkout_gst_percentage', 'checkout_global_discount_type', 'checkout_global_discount_value']);
      if (data) {
        data.forEach((row: any) => {
          if (row.setting_key === 'checkout_gst_percentage') setGstPercentage(parseFloat(row.setting_value) || 18);
          if (row.setting_key === 'checkout_global_discount_type') setGlobalDiscountType(row.setting_value);
          if (row.setting_key === 'checkout_global_discount_value') setGlobalDiscountValue(parseFloat(row.setting_value) || 0);
        });
      }
    };
    fetchPlatformCheckoutSettings();
  }, []);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isExistingMerchant, setIsExistingMerchant] = useState(false);
  const [existingMerchantId, setExistingMerchantId] = useState<string | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState('');

  useEffect(() => {
    if (user) {
        setIsExistingMerchant(true);
        setExistingMerchantId(user.id);
        setFormData(prev => ({
            ...prev,
            email: user.email || prev.email,
        }));
        
        const fetchMerchantData = async () => {
            const supabase = getSupabaseClient();
            if (supabase) {
                const { data: userData } = await supabase.from('users').select('full_name, phone').eq('id', user.id).single();
                if (userData) {
                    setFormData(prev => ({
                        ...prev,
                        name: userData.full_name || prev.name,
                        phone: userData.phone || prev.phone,
                    }));
                }
                
                if (currentTenantId) {
                   const { data: tenantData } = await supabase.from('tenants').select('business_name').eq('id', currentTenantId).single();
                   if (tenantData) {
                       setFormData(prev => ({
                           ...prev,
                           businessName: tenantData.business_name || prev.businessName
                       }));
                   }
                   
                   const { data: branchData } = await supabase.from('branches').select('address, pincode').eq('tenant_id', currentTenantId).eq('is_main_branch', true).maybeSingle();
                   if (branchData) {
                       setFormData(prev => ({
                           ...prev,
                           address: branchData.address || prev.address,
                           pincode: branchData.pincode || prev.pincode
                       }));
                   }
                }
            }
        };

        fetchMerchantData();
    }
  }, [user, currentTenantId]);

  const handleEmailBlur = async () => {
      if (!formData.email || user) return;
      setIsCheckingEmail(true);
      setDuplicateWarning('');
      const supabase = getSupabaseClient();
      if (supabase) {
          const { data: userData, error } = await supabase.from('users').select('id, full_name, phone').eq('email', formData.email.toLowerCase().trim()).maybeSingle();
          if (userData && !error) {
              setDuplicateWarning('Is email se pehle se account bana hua hai. Kripya login karein.');
          } else {
              setDuplicateWarning('');
          }
      }
      setIsCheckingEmail(false);
  };

  useEffect(() => {
    const checkDuplicateAndPricing = async () => {
      const supabase = getSupabaseClient();
      if (!supabase) return;
      
      let basePrice = 0;
      if (matchedPlan) {
          if (selectedCycle === 'yearly') basePrice = matchedPlan.priceYearly || (matchedPlan.priceMonthly * 12);
          else if (selectedCycle === 'two_years') basePrice = (matchedPlan.priceYearly ? matchedPlan.priceYearly * 2 : matchedPlan.priceMonthly * 24);
          else basePrice = matchedPlan.priceMonthly;
      } else {
          if (plan === 'Free' || displayPlanName === 'Free') basePrice = 0;
          else if (plan === 'Pro' || displayPlanName === 'Pro' || displayPlanName === 'Premium') basePrice = 999;
          else if (displayPlanName === 'Custom') basePrice = 0;
      }
      setActualPrice(basePrice);
      
      if (user) {
         const { data: subData } = await supabase.from('merchant_subscriptions')
           .select('status, plan_id, plan_type')
           .eq('merchant_id', user.id)
           .eq('module_id', moduleName)
           .eq('status', 'Active')
           .single();
         if (subData) {
            if (matchedPlan && subData.plan_id === matchedPlan.id) {
               setHasActiveSubscription(true);
            } else if (!matchedPlan && (subData.plan_id === plan || subData.plan_type === plan)) {
               setHasActiveSubscription(true);
            } else {
               setHasActiveSubscription(false);
            }
         } else {
            setHasActiveSubscription(false);
         }
      }
    };
    checkDuplicateAndPricing();
  }, [moduleName, plan, user, matchedPlan, selectedCycle, displayPlanName]);

  const applyPromo = async () => {
    if (!promoCode) return;
    const supabase = getSupabaseClient();
    if (!supabase) return;

    const { data: coupon, error } = await supabase
      .from('promo_codes')
      .select('*')
      .eq('code', promoCode.toUpperCase())
      .eq('is_active', true)
      .maybeSingle();

    if (error || !coupon) {
      alert("Invalid or inactive promo code.");
      setPromoDiscount(0);
      return;
    }

    const modName = getOfficialModuleName(moduleName, moduleName);
    const moduleMatch = coupon.applicable_module === 'All' || coupon.applicable_module === modName || coupon.applicable_module === moduleName;
    const planMatch = coupon.applicable_plan === 'All' || coupon.applicable_plan === plan;

    if (!moduleMatch || !planMatch) {
      alert("This promo code is not applicable to the selected plan or module.");
      setPromoDiscount(0);
      return;
    }

    const discountType = coupon.discount_type || (coupon.discount_percentage ? 'percentage' : 'fixed');
    const discountValue = coupon.discount_value ?? coupon.discount_percentage ?? coupon.fixed_discount ?? 0;

    if (discountType === 'fixed') {
      setPromoDiscount(Number(discountValue));
    } else {
      setPromoDiscount((actualPrice * Number(discountValue)) / 100);
    }
    alert("Promo code applied! (Final price is securely re-verified at checkout.)");
  };

  const getPrice = () => {
    const afterPromo = Math.max(0, actualPrice - promoDiscount);
    const globalDiscountAmount = globalDiscountType === 'percent'
      ? (afterPromo * globalDiscountValue) / 100
      : globalDiscountType === 'fixed' ? globalDiscountValue : 0;
    return Math.max(0, afterPromo - globalDiscountAmount);
  };

  const getFinalPayableAmount = () => {
    return getPrice() * (1 + gstPercentage / 100);
  };

  // =========================================================================
  // STEP 4 — create-razorpay-order (Supabase Edge Function - paid plans)
  // Body: { purpose: "merchant_signup", pending_signup_id: pending_id, billing_cycle }
  // Response: { razorpay_order_id, razorpay_key_id, amount, currency, payment_order_id }
  // =========================================================================
  const handleCreateRazorpayOrder = async (pendingId: string, cycle: string) => {
    setIsProcessing(true);
    try {
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        alert('Razorpay SDK load nahi ho paya. Kripya internet connection check karein.');
        setIsProcessing(false);
        return;
      }

      const normalizedCycle = (cycle === 'yearly' || cycle === 'annual' || cycle === 'One Year' || cycle === 'two_years') ? 'yearly' : 'monthly';

      const { data: orderData, error: orderErr } = await invokeEdgeFunction('create-razorpay-order', {
        purpose: 'merchant_signup',
        pending_signup_id: pendingId,
        billing_cycle: normalizedCycle
      });

      if (orderErr || !orderData) {
        alert(orderErr || 'Payment order create nahi ho paya. Kripya dobara koshish karein.');
        setIsProcessing(false);
        return;
      }

      // STEP 5 — Razorpay Checkout & Success Callback
      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: 'BahiBox',
        description: `Subscription for ${displayPlanName}`,
        order_id: orderData.razorpay_order_id,
        handler: function () {
          // Payment safal hote hi dikhao:
          // "Payment mil gaya! Aapke email par 1-2 minute mein ek invite link aayega — usse apna password set karke login karein." -> phir /login par redirect kar do.
          setIsProcessing(false);
          setSignupSuccess({
            title: 'Payment Received!',
            message: 'Payment mil gaya! Aapke email par 1-2 minute mein ek invite link aayega — usse apna password set karke login karein.',
            email: formData.email,
            autoRedirect: true
          });
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#0f172a'
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          }
        }
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.on('payment.failed', function (resp: any) {
        alert(resp?.error?.description || 'Payment fail ho gaya. Kripya dobara try karein.');
        setIsProcessing(false);
      });
      rzp.open();

    } catch (err: any) {
      console.error('Payment start error:', err);
      alert(err.message || 'Payment start karne mein dikkat aayi.');
      setIsProcessing(false);
    }
  };

  // =========================================================================
  // STEP 3 — verify-signup (Supabase Edge Function)
  // Body: { pending_id, email_otp }
  // =========================================================================
  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) {
      setOtpError('Kripya 6-digit OTP darj karein');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError('');

    try {
      const { data: verifyData, error: verifyErr } = await invokeEdgeFunction('verify-signup', {
        pending_id: pendingSignupId,
        email_otp: otpValue.trim()
      });

      if (verifyErr || !verifyData) {
        setOtpError(verifyErr || 'OTP verify karne mein dikkat aayi. Dobara try karein.');
        setIsVerifyingOtp(false);
        return;
      }

      // Free plan response: { success:true, account_created:true, invited:true, user_id, tenant_id, message }
      // -> modal band karo, dikhao: "OTP verify ho gaya! Aapke email par ek invite link bheja gaya hai — usse apna password set karke login karein." -> /login ka link do.
      if (verifyData.account_created === true || verifyData.invited === true) {
        setShowOtpModal(false);
        setIsVerifyingOtp(false);
        setIsProcessing(false);
        setSignupSuccess({
          title: 'Account Created Successfully!',
          message: 'OTP verify ho gaya! Aapke email par ek invite link bheja gaya hai — usse apna password set karke login karein.',
          email: formData.email,
          autoRedirect: false
        });
        return;
      }

      // Paid plan response: { success:true, account_created:false, pending_id, plan_id, billing_cycle }
      // -> seedha Step 4 par jaao.
      setShowOtpModal(false);
      setIsVerifyingOtp(false);

      const targetCycle = verifyData.billing_cycle || selectedCycle;
      await handleCreateRazorpayOrder(pendingSignupId, targetCycle);

    } catch (err: any) {
      setOtpError(err.message || 'OTP verification error');
      setIsVerifyingOtp(false);
    }
  };

  const handlePincodeBlur = async () => {
    const trimmedPincode = (formData.pincode || '').trim();
    if (!trimmedPincode) {
      setPincodeError("Please enter your pincode.");
      return;
    }
    const supabase = getSupabaseClient();
    if (supabase) {
      setIsCheckingPincode(true);
      try {
        const { data: pincodeCheck, error: pError } = await supabase.rpc('check_merchant_signup_pincode', {
          p_pincode: trimmedPincode
        });
        setIsCheckingPincode(false);
        if (pError || !pincodeCheck || pincodeCheck.allowed === false) {
          setPincodeError("This pincode is not yet serviceable. We will be launching here soon.");
        } else {
          setPincodeError('');
        }
      } catch (err) {
        setIsCheckingPincode(false);
        setPincodeError("This pincode is not yet serviceable. We will be launching here soon.");
      }
    }
  };

  // =========================================================================
  // MAIN CHECKOUT HANDLER
  // If guest merchant: runs Step 1 (start-signup Edge Function)
  // If existing logged-in merchant: upgrades subscription
  // =========================================================================
  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hasActiveSubscription) {
      alert("You already have an active subscription for this module.");
      return;
    }

    // Pincode validation: required and must be serviceable
    const trimmedPincode = (formData.pincode || '').trim();
    if (!trimmedPincode) {
      setPincodeError("Please enter your pincode.");
      return;
    }

    const supabase = getSupabaseClient();
    if (supabase) {
      setIsCheckingPincode(true);
      try {
        const { data: pincodeCheck, error: pError } = await supabase.rpc('check_merchant_signup_pincode', {
          p_pincode: trimmedPincode
        });
        setIsCheckingPincode(false);
        if (pError || !pincodeCheck || pincodeCheck.allowed === false) {
          setPincodeError("This pincode is not yet serviceable. We will be launching here soon.");
          return;
        }
        setPincodeError('');
      } catch (err) {
        setIsCheckingPincode(false);
        setPincodeError("This pincode is not yet serviceable. We will be launching here soon.");
        return;
      }
    }

    // Existing logged-in user renewal/upgrade
    if (user?.id) {
      setIsProcessing(true);
      try {
        const supabase = getSupabaseClient();
        if (!supabase) throw new Error("Database client not available");

        const { data: matchedPlans } = await supabase
          .from('subscription_plans')
          .select('id')
          .eq('module_name', moduleName)
          .eq('name', plan);
        const planId = matchedPlans && matchedPlans.length > 0 ? matchedPlans[0].id : null;

        const finalAmount = getFinalPayableAmount();
        let resolvedTenantId = currentTenantId;
        if (!resolvedTenantId) {
          const { data: roles } = await supabase
            .from('user_tenant_roles')
            .select('tenant_id')
            .eq('user_id', user.id)
            .eq('role_name', 'owner');
          if (roles && roles.length > 0) resolvedTenantId = roles[0].tenant_id;
        }

        if (finalAmount === 0 || plan === 'Free') {
          const { data: { session } } = await supabase.auth.getSession();
          const response = await fetch('/api/activate-free-plan', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token || ''}`
            },
            body: JSON.stringify({
              plan_id: planId || plan,
              tenant_id: resolvedTenantId,
              promo_code: promoDiscount > 0 ? promoCode.toUpperCase() : undefined
            })
          });

          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            alert('Failed to activate plan: ' + (errData.error || 'Unknown error'));
            setIsProcessing(false);
            return;
          }
          alert('Subscription Activated Successfully!');
          navigate('/merchant-dashboard', { state: { module: moduleName } });
          return;
        }

        const isLoaded = await loadRazorpayScript();
        if (!isLoaded) {
          alert('Failed to load Razorpay SDK.');
          setIsProcessing(false);
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch('/api/create-razorpay-order', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || ''}`
          },
          body: JSON.stringify({
            plan_id: planId || plan,
            billing_cycle: selectedCycle,
            tenant_id: resolvedTenantId,
            branch_id: null,
            promo_code: promoDiscount > 0 ? promoCode.toUpperCase() : undefined
          })
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to create Razorpay order');
        }

        const orderData = await response.json();
        const options = {
          key: orderData.razorpay_key_id,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'BahiBox',
          description: `Subscription for ${displayPlanName}`,
          order_id: orderData.razorpay_order_id,
          handler: async function (resp: any) {
            await supabase.rpc('capture_payment', {
              p_payment_order_id: orderData.payment_order_id,
              p_razorpay_payment_id: resp.razorpay_payment_id,
              p_method: 'razorpay',
              p_gateway_fee: 0,
              p_raw_response: resp
            });
            alert('Payment successful! Your plan has been upgraded.');
            navigate('/merchant-dashboard', { state: { module: moduleName } });
          },
          prefill: {
            name: formData.name,
            email: formData.email,
            contact: formData.phone
          },
          theme: { color: '#0f172a' }
        };

        const rzp = new (window as any).Razorpay(options);
        rzp.on('payment.failed', () => alert('Payment failed. Please try again.'));
        rzp.open();
        setIsProcessing(false);
        return;
      } catch (err: any) {
        alert(err.message || "An error occurred during checkout.");
        setIsProcessing(false);
        return;
      }
    }

    // =========================================================================
    // STEP 1 — start-signup (Supabase Edge Function for new merchants)
    // Body: { email, phone, full_name, business_name, business_type, plan_id, billing_cycle }
    // (password bilkul mat bhejna — form mein field hai hi nahi, backend bhi expect nahi karta)
    // =========================================================================
    setIsProcessing(true);
    try {
      const supabase = getSupabaseClient();

      // Resolve plan UUID
      let planId = matchedPlan?.id;
      if (!planId && supabase) {
        const { data: dbPlans } = await supabase
          .from('subscription_plans')
          .select('id, name, module_name, is_active')
          .eq('module_name', moduleName)
          .eq('name', plan);
        if (dbPlans && dbPlans.length > 0) {
          planId = dbPlans[0].id;
        }
      }

      if (!planId) {
        const found = plans.find(p => p.id === plan || p.name.toLowerCase() === plan.toLowerCase());
        if (found) planId = found.id;
      }

      if (!planId) {
        alert("Subscription plan not found. Please re-select from Pricing.");
        setIsProcessing(false);
        return;
      }

      const businessType = getBusinessTypeFromModule(moduleName);
      const normalizedCycle = (selectedCycle === 'yearly' || selectedCycle === 'annual' || selectedCycle === 'One Year' || selectedCycle === 'two_years') ? 'yearly' : 'monthly';

      const { data: startData, error: startErr } = await invokeEdgeFunction('start-signup', {
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        full_name: formData.name.trim(),
        business_name: (formData.businessName || `${formData.name}'s Business`).trim(),
        business_type: businessType,
        plan_id: planId,
        billing_cycle: normalizedCycle,
        address: formData.address.trim(),
        pincode: trimmedPincode
      });

      if (startErr || !startData) {
        const errMsg = startErr || 'Signup shuru karne mein dikkat aayi. Dobara koshish karein.';
        alert(errMsg);
        if (errMsg.includes('pehle se account') || errMsg.includes('login karein')) {
          navigate('/login');
        }
        setIsProcessing(false);
        return;
      }

      // STEP 2 — OTP Modal
      // start-signup success hote hi ek overlay/modal kholo:
      // "OTP aapke email par bheja gaya hai" + 6-digit input box + "Verify" button. pending_id ko state mein rakho.
      setPendingSignupId(startData.pending_id);
      setOtpValue('');
      setOtpError('');
      setShowOtpModal(true);
      setIsProcessing(false);

    } catch (err: any) {
      console.error('Error starting signup:', err);
      alert(err.message || 'Error occurred while starting signup.');
      setIsProcessing(false);
    }
  };

  return (
    signupSuccess ? (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8 text-center border border-slate-200 dark:border-slate-800">
          <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">{signupSuccess.title}</h2>
          {signupSuccess.email && (
            <p className="font-semibold text-primary text-sm mb-4">{signupSuccess.email}</p>
          )}
          <p className="text-slate-600 dark:text-slate-400 text-base mb-8 leading-relaxed">
            {signupSuccess.message}
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 text-base font-bold bg-primary text-white hover:opacity-90 transition-opacity"
            >
              Go to Login
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full h-11"
            >
              Back to Home
            </Button>
          </div>
          {signupSuccess.autoRedirect && (
            <p className="text-xs text-slate-400 mt-4">
              Redirecting to login in 5 seconds...
            </p>
          )}
        </div>
      </div>
    ) : (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 font-sans text-slate-800 dark:text-slate-200 flex flex-col items-center py-12 px-4 relative">
      <div className="w-full max-w-4xl flex items-center gap-3 mb-8 cursor-pointer" onClick={() => navigate('/')}>
        <img src="/logolight.png" alt="BahiBox Logo" className="h-24 md:h-32 dark:hidden object-contain" />
        <img src="/logodark.png" alt="BahiBox Logo" className="h-24 md:h-32 hidden dark:block object-contain" />
        <span className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 ml-2">Checkout</span>
      </div>

      <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Form Section */}
        <Card>
          <CardHeader>
            <CardTitle>Billing Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCheckout} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Business Name</label>
                <Input 
                  required 
                  placeholder="Sharma General Store" 
                  value={formData.businessName}
                  onChange={e => setFormData({...formData, businessName: e.target.value})}
                  disabled={isExistingMerchant}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Full Name</label>
                <Input 
                  required 
                  placeholder="Rahul Kumar" 
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  disabled={isExistingMerchant}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone Number</label>
                <Input 
                  required 
                  placeholder="+91 XXXXX XXXXX" 
                  value={formData.phone}
                  onChange={e => setFormData({...formData, phone: e.target.value})}
                  disabled={isExistingMerchant}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Address</label>
                <Input 
                  required 
                  placeholder="123 Main Street" 
                  value={formData.address}
                  onChange={e => setFormData({...formData, address: e.target.value})}
                  disabled={isExistingMerchant}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Pincode</label>
                <div className="relative">
                  <Input 
                    required 
                    placeholder="e.g. 400001" 
                    value={formData.pincode}
                    onChange={e => {
                      setFormData({...formData, pincode: e.target.value});
                      if (pincodeError) setPincodeError('');
                    }}
                    onBlur={handlePincodeBlur}
                    onInvalid={(e) => {
                      e.preventDefault();
                      setPincodeError("Please enter your pincode.");
                    }}
                    disabled={isExistingMerchant}
                    className={pincodeError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                  />
                  {isCheckingPincode && <span className="absolute right-3 top-2.5 text-xs text-slate-500 dark:text-slate-400">Checking...</span>}
                </div>
                {pincodeError && (
                  <p className="text-sm text-red-600 mt-1 font-medium">{pincodeError}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Email Address</label>
                <div className="relative">
                  <Input 
                    required 
                    type="email"
                    placeholder="rahul@example.com" 
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    onBlur={handleEmailBlur}
                    disabled={!!user || isCheckingEmail}
                  />
                  {isCheckingEmail && <span className="absolute right-3 top-2.5 text-xs text-slate-500 dark:text-slate-400">Checking...</span>}
                </div>
                {duplicateWarning && (
                  <p className="text-sm text-red-600 mt-1 font-medium">{duplicateWarning}</p>
                )}
              </div>

              {hasActiveSubscription && (
                 <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-md text-sm border border-red-200">
                    You already have an active subscription for this module.
                 </div>
              )}

              <Button type="submit" className="w-full mt-6 h-12 text-lg" disabled={isProcessing || hasActiveSubscription}>
                {isProcessing ? 'Processing...' : hasActiveSubscription ? 'Already Subscribed' : (displayPlanName === 'Custom' ? 'Submit Request' : (getPrice() === 0 ? `Activate ${displayPlanName} Plan` : `Pay ₹${getFinalPayableAmount().toFixed(2)} Now`))}
              </Button>
              <Button type="button" variant="outline" className="w-full mt-2" onClick={() => navigate('/')}>
                Back to Home Page
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Order Summary Section */}
        <Card className="h-fit bg-slate-900 text-white border-0">
          <CardHeader>
            <CardTitle>Order Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-start pb-4 border-b border-white/20">
                <div>
                  <p className="font-semibold text-lg">{moduleName} Module</p>
                  <p className="text-sm text-white/60 mb-2">{displayPlanName.replace(/\s*Plan$/i, '')} Plan</p>
                  <div className="relative inline-block">
                    <select 
                      className="appearance-none bg-white/10 border border-white/20 text-white pr-8 pl-2 py-1 outline-none cursor-pointer hover:bg-white/20 rounded text-sm transition-colors"
                      value={selectedCycle}
                      onChange={(e) => setSelectedCycle(e.target.value)}
                    >
                      <option value="monthly" className="text-slate-900 dark:text-slate-100">Monthly</option>
                      <option value="yearly" className="text-slate-900 dark:text-slate-100">1 Year</option>
                      <option value="two_years" className="text-slate-900 dark:text-slate-100">2 Years</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-white pointer-events-none" />
                  </div>
                </div>
                <p className="font-bold text-xl mt-1">₹{getFinalPayableAmount().toFixed(2)}</p>
              </div>

              <div className="flex justify-between items-center text-sm text-white/60">
                <p>Subtotal</p>
                <p>₹{actualPrice}</p>
              </div>
              
              {promoDiscount > 0 && (
                <div className="flex justify-between items-center text-sm text-emerald-400">
                  <p>Promo Discount</p>
                  <p>-₹{promoDiscount.toFixed(2)}</p>
                </div>
              )}

              {globalDiscountType !== 'none' && globalDiscountValue > 0 && (
                <div className="flex justify-between items-center text-sm text-emerald-400">
                  <p>Special Discount</p>
                  <p>-₹{(Math.max(0, actualPrice - promoDiscount) - getPrice()).toFixed(2)}</p>
                </div>
              )}
              <div className="flex justify-between items-center text-sm text-white/60">
                <p>Taxes (GST {gstPercentage}%)</p>
                <p>₹{(getPrice() * (gstPercentage / 100)).toFixed(2)}</p>
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-white/20 text-xl font-bold">
                <p>Total</p>
                <p>₹{getFinalPayableAmount().toFixed(2)}</p>
              </div>

              <div className="pt-4 mt-2">
                <p className="text-sm text-white/60 mb-2">Have a Promo Code?</p>
                <div className="flex gap-2">
                  <Input 
                    placeholder="Enter code" 
                    value={promoCode} 
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-10"
                  />
                  <Button type="button" onClick={applyPromo} variant="secondary" className="h-10 px-4">
                    Apply
                  </Button>
                </div>
              </div>

              {plan === 'Free' && getPrice() > 0 && (
                <div className="mt-6 p-4 bg-white dark:bg-slate-950/10 rounded-lg text-sm text-white/80">
                  <p><strong>Note:</strong> A nominal fee of ₹1 is charged to verify your payment method. This helps us prevent spam and abuse.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========================================================================= */}
      {/* STEP 2 — OTP Modal */}
      {/* ========================================================================= */}
      {showOtpModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 relative animate-in fade-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={() => {
                if (!isVerifyingOtp) setShowOtpModal(false);
              }}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-primary">
              <Mail className="w-7 h-7" />
            </div>

            <h3 className="text-xl font-bold text-center mb-1">OTP Verification</h3>
            <p className="text-slate-600 dark:text-slate-300 text-sm text-center mb-1">
              OTP aapke email par bheja gaya hai
            </p>
            <p className="text-center text-sm font-semibold text-primary mb-6 break-all">
              {formData.email}
            </p>

            <form onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider text-center mb-2">
                  Enter 6-Digit OTP
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoFocus
                  value={otpValue}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtpValue(val);
                    if (otpError) setOtpError('');
                  }}
                  placeholder="• • • • • •"
                  className="text-center font-mono text-2xl tracking-[0.4em] h-14 font-bold border-2 border-slate-300 dark:border-slate-700 focus:border-primary"
                  disabled={isVerifyingOtp}
                />
              </div>

              {otpError && (
                <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 text-sm rounded-lg font-medium text-center">
                  {otpError}
                </div>
              )}

              <Button
                type="submit"
                disabled={otpValue.length !== 6 || isVerifyingOtp}
                className="w-full h-12 text-base font-bold"
              >
                {isVerifyingOtp ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" /> Verifying...
                  </span>
                ) : (
                  'Verify'
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowOtpModal(false)}
                disabled={isVerifyingOtp}
                className="w-full text-slate-500 hover:text-slate-700 dark:text-slate-400 text-sm"
              >
                Cancel / Edit Details
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  ));
}

